/**
 * Утилиты для расчета недели беременности
 */

/**
 * Рассчитывает текущую неделю беременности на основе ПДР (предполагаемой даты родов)
 * 
 * Стандартная беременность длится 40 недель (280 дней) от первого дня последней менструации.
 * Формула расчета от ПДР:
 * - Если ПДР в будущем: неделя = 40 - (дни до ПДР / 7)
 * - Если ПДР в прошлом: неделя = 40 + (дни после ПДР / 7)
 * 
 * Используется округление до ближайшего целого для более точного расчета.
 * 
 * @param dueDate - Дата ПДР в формате YYYY-MM-DD или Date объект
 * @returns Номер недели беременности (от 1 до 42), или null если дата некорректна
 */
export function calculateWeekOfPregnancy(dueDate: string | Date | null | undefined): number | null {
  if (!dueDate) {
    return null;
  }

  try {
    const dueDateObj = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    
    // Проверка валидности даты
    if (isNaN(dueDateObj.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDateOnly = new Date(dueDateObj);
    dueDateOnly.setHours(0, 0, 0, 0);

    // Разница в миллисекундах
    const diffMs = dueDateOnly.getTime() - today.getTime();
    
    // Разница в днях
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Разница в неделях (округление до ближайшего целого для точности)
    // Если diffDays положительный (ПДР в будущем) - вычитаем недели
    // Если diffDays отрицательный (ПДР в прошлом) - прибавляем недели
    const diffWeeks = Math.round(diffDays / 7);
    
    // Текущая неделя беременности
    // Стандартная беременность = 40 недель от первого дня последней менструации
    // Если ПДР в будущем (diffWeeks > 0): неделя = 40 - diffWeeks
    // Если ПДР в прошлом (diffWeeks < 0): неделя = 40 - diffWeeks = 40 + |diffWeeks|
    const week = 40 - diffWeeks;
    
    // Логирование для отладки
    console.log('[calculateWeekOfPregnancy]', {
      dueDate: dueDateOnly.toISOString().split('T')[0],
      today: today.toISOString().split('T')[0],
      diffDays,
      diffWeeks,
      calculatedWeek: week,
    });
    
    // Ограничиваем диапазон от 1 до 42 недель
    if (week < 1) {
      console.log('[calculateWeekOfPregnancy] Week < 1, returning 1');
      return 1; // Минимум 1 неделя
    }
    if (week > 42) {
      console.log('[calculateWeekOfPregnancy] Week > 42, returning 42');
      return 42; // Максимум 42 недели (после этого обычно роды уже произошли)
    }
    
    return week;
  } catch (error) {
    console.error('Error calculating week of pregnancy:', error);
    return null;
  }
}

/**
 * Рассчитывает неделю беременности на основе даты начала беременности
 * (альтернативный метод, если нет ПДР)
 * 
 * @param startDate - Дата начала беременности (первый день последней менструации)
 * @returns Номер недели беременности (от 1 до 42), или null если дата некорректна
 */
export function calculateWeekFromStart(startDate: string | Date | null | undefined): number | null {
  if (!startDate) {
    return null;
  }

  try {
    const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
    
    // Проверка валидности даты
    if (isNaN(startDateObj.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDateOnly = new Date(startDateObj);
    startDateOnly.setHours(0, 0, 0, 0);

    // Разница в миллисекундах
    const diffMs = today.getTime() - startDateOnly.getTime();
    
    // Разница в днях
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Разница в неделях (округление вниз)
    const diffWeeks = Math.floor(diffDays / 7) + 1; // +1 потому что первая неделя начинается с 1
    
    // Ограничиваем диапазон от 1 до 42 недель
    if (diffWeeks < 1) {
      return 1;
    }
    if (diffWeeks > 42) {
      return 42;
    }
    
    return diffWeeks;
  } catch (error) {
    console.error('Error calculating week from start date:', error);
    return null;
  }
}

/**
 * Получает актуальную неделю беременности пользователя
 * Приоритет: расчет на основе ПДР, если нет ПДР - используется сохраненное значение
 * 
 * @param user - Объект пользователя с полями due_date и week_of_pregnancy
 * @returns Номер недели беременности или null
 */
export function getCurrentWeekOfPregnancy(user: {
  due_date?: string | Date | null;
  week_of_pregnancy?: number | null;
}): number | null {
  // Сначала пытаемся рассчитать на основе ПДР
  if (user.due_date) {
    const calculatedWeek = calculateWeekOfPregnancy(user.due_date);
    if (calculatedWeek !== null) {
      return calculatedWeek;
    }
  }
  
  // Если ПДР нет или расчет не удался, используем сохраненное значение
  return user.week_of_pregnancy ?? null;
}
